import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import frame_1 from '../../../../assets/images/selfieFrames/en/frame_1.png';
import frame_2 from '../../../../assets/images/selfieFrames/en/frame_2.png';
import frame_3 from '../../../../assets/images/selfieFrames/en/frame_3.png';
import frame_4 from '../../../../assets/images/selfieFrames/en/frame_4.png';
import frame_5 from '../../../../assets/images/selfieFrames/en/frame_5.png';
import frame_6 from '../../../../assets/images/selfieFrames/en/frame_6.png';
import frame_7 from '../../../../assets/images/selfieFrames/en/frame_7.png';
import frame_1_pl from '../../../../assets/images/selfieFrames/pl/frame_1_pl.png';
import frame_2_pl from '../../../../assets/images/selfieFrames/pl/frame_2_pl.png';
import frame_3_pl from '../../../../assets/images/selfieFrames/pl/frame_3_pl.png';
import frame_4_pl from '../../../../assets/images/selfieFrames/pl/frame_4_pl.png';
import { useBoundingRect } from '@/util/useBoundingRect.ts';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { useResponsiveCanvasIos } from '@/components/slides/Selfie/iOS/useResponsiveCanvasIos.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';

export type Frame = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/*
  canvasDrawScaledFrame:

  Takes a Canvas painting 2d Context and the Imported source of an image.

  Creates a new Image to draw onto the Canvas, sourced by the "frameSrc" parameter.
  Calculations the width/height ration and scales + centers the image on the canvas

 */
const canvasDrawScaledFrame = (ctx: CanvasRenderingContext2D, frameSrc: any) => {
  const frameImg = new Image();
  frameImg.onload = () => {
    const canvas = ctx.canvas;
    const hRatio = canvas.width / frameImg.width;
    const vRatio = canvas.height / frameImg.height;
    const ratio = Math.min(hRatio, vRatio);
    const centerShift_x = (canvas.width - frameImg.width * ratio) / 2;
    const centerShift_y = (canvas.height - frameImg.height * ratio) / 2;

    ctx.drawImage(
      frameImg,
      0,
      0,
      frameImg.width,
      frameImg.height,
      centerShift_x,
      centerShift_y,
      frameImg.width * ratio,
      frameImg.height * ratio
    );
  };
  frameImg.src = frameSrc;
};

const clearCanvas = (ctx: CanvasRenderingContext2D) => {
  const canvas = ctx.canvas;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

const framesByLocale: Record<string, string[]> = {
  DE: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  GB: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  CL: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  IN: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  US: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  ZA: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  PL: [frame_1_pl, frame_2_pl, frame_3_pl, frame_4_pl],
};

export const useSelfieCanvasIos = () => {
  const [parentRef, parentRect] = useBoundingRect();
  const { showLayer } = useLayer();

  const responsive = useResponsiveCanvasIos(parentRect);

  const mainCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameIndex = useRef(0);
  const snapshotCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hasSnapshot, setHasSnapshot] = useState<boolean>(false);
  const { currentSlide } = useNavigation();
  const { eventConfig } = useEventConfig();
  const [shareButtonWasClicked, setShareButtonWasClicked] = useState<boolean>(false);

  const [nextFrameSrc, setNextFrameSrc] = useState(frame_2);
  const [prevFrameSrc, setPrevFrameSrc] = useState(frame_4);

  const locale = eventConfig?.meta.country ?? 'DE';

  useEffect(() => {
    setTimeout(init, 100);
    return teardown;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (snapshotCanvasRef.current) {
      snapshotCanvasRef.current!.width = responsive.width;
      snapshotCanvasRef.current!.height = responsive.height;
    }
  }, [responsive.width, responsive.height]);

  // Init Fn creates a Temporary Canvas, that stores the Video-Screenshot-Image
  // Then It paints the currently selected "Frame" border image on the Main Canvas
  // Then it starts the Videostream of the camera, that fills the <video> tag
  // (which in turn is positioned BELOW the frame-canvas so it looks like a preview
  const init = () => {
    createTempCanvas();
    drawCurrentFrame();
  };

  // When leaving the overlay/slide we stop the camera, which is important
  // to build trust (and save resources etc)
  const teardown = () => {
    // Noop
  };

  // Whenever videoSize or parentRect change values, i.e. when the screen
  // is resized or flipped to landscape mode, we drop the existing video-screenshot
  useEffect(() => {
    clearSnapshot();
  }, [parentRect]);

  // Data structure on a temporary <canvas> element where we paint the current
  // "screenshot" (snapshot) of the video for preview. From there it gets
  // drawn onto the main-canvas (immediately)
  const createTempCanvas = () => {
    snapshotCanvasRef.current = document.createElement('canvas');
    snapshotCanvasRef.current!.width = responsive.width || 2000;
    snapshotCanvasRef.current!.height = responsive.height || 2000;
  };

  const getMainContext = (): CanvasRenderingContext2D | null => {
    if (mainCanvasRef.current === null) {
      return null;
    }
    return mainCanvasRef.current!.getContext('2d');
  };

  const getSnapshotContext = (): CanvasRenderingContext2D | null => {
    if (snapshotCanvasRef.current === null) {
      return null;
    }
    return snapshotCanvasRef.current!.getContext('2d');
  };

  // Load the Image of a DKMS frame and paint it onto the main <canvas>
  const drawCurrentFrame = () => {
    const ctx = getMainContext();
    if (!ctx) return;

    const frames = framesByLocale[locale];
    if (!frames || frames.length === 0) return;

    const frameCount = frames.length;
    const currentFrame = frames[frameIndex.current % frameCount];

    canvasDrawScaledFrame(ctx, currentFrame);

    const nextIndex = (frameIndex.current + 1) % frameCount;
    const prevIndex = (frameIndex.current - 1 + frameCount) % frameCount;

    setNextFrameSrc(frames[nextIndex]);
    setPrevFrameSrc(frames[prevIndex]);
  };

  const getFrameCount = (): number => {
    const frames = framesByLocale[locale];
    if (!frames || frames.length === 0) return 0;

    return frames.length;
  };

  const selectNextFrame = () => {
    frameIndex.current = (frameIndex.current + 1) % getFrameCount();
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearCanvas(mainCtx);
    paintSnapshotOnMain();
    drawCurrentFrame();
  };

  const selectPrevFrame = () => {
    const frameCount = getFrameCount();

    frameIndex.current = (frameIndex.current - 1 + frameCount) % frameCount;
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearCanvas(mainCtx);
    paintSnapshotOnMain();
    drawCurrentFrame();
  };

  // When
  const paintSnapshotOnMain = () => {
    const mainCtx = getMainContext();
    const snapCtx = getSnapshotContext();
    if (!mainCtx || !snapCtx) {
      return;
    }

    const storageCanvas = snapCtx.canvas;
    mainCtx.drawImage(snapshotCanvasRef.current!, 0, 0, storageCanvas.width, storageCanvas.height);
  };

  const storeSnapshotImage = (file: File) => {
    snapshotCanvasRef.current!.width = responsive.width;
    snapshotCanvasRef.current!.height = responsive.height;
    const snapCtx = getSnapshotContext();

    if (!snapCtx) {
      return;
    }

    const drawImage = (resolve: (value: unknown) => void) => {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();

        // When the file is loaded
        reader.onload = (event) => {
          const img = new Image();
          img.onload = function () {
            const canvas = snapCtx.canvas;
            const hRatio = canvas.width / img.width;
            const vRatio = canvas.height / img.height;
            const ratio = Math.min(hRatio, vRatio);
            const centerShift_x = (canvas.width - img.width * ratio) / 2;
            const centerShift_y = (canvas.height - img.height * ratio) / 2;

            snapCtx.drawImage(
              img,
              0,
              0,
              img.width,
              img.height,
              centerShift_x,
              centerShift_y,
              img.width * ratio,
              img.height * ratio
            );
            resolve(undefined);
          };

          img.src = (event.target?.result || '') as string;
        };

        // Read the image file as a data URL
        reader.readAsDataURL(file);
      }
    };

    return new Promise(drawImage);
  };

  const clearSnapshotImage = () => {
    const snapCtx = getSnapshotContext();
    if (!snapCtx) {
      return;
    }
    const canvas = snapCtx.canvas;
    snapCtx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const takeSnapshot: ChangeEventHandler<HTMLInputElement> = (changeEvent) => {
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }

    const file = changeEvent.target.files?.[0];
    if (!file) {
      return;
    }

    clearCanvas(mainCtx);
    // Fuck you Typeshit. THIS IS NOT POSSIBLY UNDEFINED
    // @ts-ignore
    storeSnapshotImage(file).then(() => {
      paintSnapshotOnMain();
      drawCurrentFrame();
      setHasSnapshot(true);
    });
  };

  const clearSnapshot = () => {
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearSnapshotImage();
    clearCanvas(mainCtx);
    drawCurrentFrame();
    setHasSnapshot(false);
  };

  // The share menu. Grab the image from main <canvas>
  // convert it to a File and send it to the navigator.share native menu where
  // the user can now select InstaChat or Twix or whatever she has installed on
  // their phone. Suggestions for title/url are taken from the slide.yaml config
  const shareSnapshot = () => {
    setShareButtonWasClicked(true);
    mainCanvasRef.current!.toBlob((blob) => {
      if (!blob) {
        throw 'Something went wrong with the blob.';
        return;
      }
      const file = new File([blob], 'dkms.png', {
        type: blob.type,
        lastModified: new Date().getTime(),
      });

      const shareData: ShareData = {
        title: (currentSlide?.selfiesharetitle as string) || 'DKMS',
        text: (currentSlide?.selfiesharetext as string) || 'Saving lives!',
        url: (currentSlide?.selfieshareurl as string) || 'https://dkms.de/',
        files: [file],
      };

      try {
        const canShare = navigator.canShare(shareData);
        if (canShare) {
          navigator.share(shareData).catch((e) => {
            if (e.toString().includes('AbortError')) {
              // Normal "cancel" event. Nothing to see here
            } else {
              console.log(e);
            }
          });
        } else {
          showLayer({ ref: '@sharingNotSupported' });
        }
      } catch (_e) {
        showLayer({ ref: '@sharingNotSupported' });
      }
    });
  };

  return {
    canvasRef: mainCanvasRef,
    parentRef,
    nextFrame: selectNextFrame,
    prevFrame: selectPrevFrame,
    takeSnapshot,
    clearSnapshot,
    shareSnapshot,
    hasSnapshot,
    width: responsive.width,
    height: responsive.height,
    restartCamera: init,
    shareButtonWasClicked,
    nextFrameSrc,
    prevFrameSrc,
  };
};
